import axios from 'axios';
import { fetchBaseUrl } from 'core/utils/common';

const baseURL = fetchBaseUrl({port: 8000});
const API_BASE_URL = `${baseURL}/socai/api`;
const DEFAULT_HEADERS = {
  'Content-Type': 'application/json',
  'x-api-key': 'dev-key'
};

export interface FilePayload {
  file_content: string;
  file_name: string;
  mime_type: string;
  file_size: number;
}

export interface UploadResponse {
  status: number;
  data: any;
}

// Fetch capabilities/tasks
export const fetchCapabilities = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/tasks`, {
      headers: DEFAULT_HEADERS
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching capabilities:", error);
    throw error;
  }
};

// Fetch conversations
export const fetchConversations = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/context/conversations`, {
      headers: DEFAULT_HEADERS
    });
    return response.data.conversations;
  } catch (error) {
    console.error("Error fetching conversations:", error);
    throw error;
  }
};

// Upload file
export const uploadFile = async (userInput: string, conversationId: string, file: FilePayload): Promise<UploadResponse> => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/upload/orchestrator`,
      {
        user_input: userInput,
        conversation_id: conversationId,
        file: file
      },
      {
        headers: DEFAULT_HEADERS,
        params: { stream: false }
      }
    );
    return response.data.response;
  } catch (error) {
    console.error("Error uploading file:", error);
    throw error;
  }
};

// Send chat message
// ...existing code...

export const sendMessage = async ({userInput,conversationId}) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/serve`,
      {
        user_input: userInput,
        conversation_id: conversationId,
      },
      {
        headers: DEFAULT_HEADERS,
        params: { stream: false }
      }
    );
    return response.data.response || [];
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

export const executeTask = async (taskId: string) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/task/${taskId}`,
      {
        headers: DEFAULT_HEADERS
      }
    );
    return response.data.response || [];
  } catch (error) {
    console.error('Error executing task:', error);
    throw error;
  }
};
// Categorize conversations by date
export const categorizeConversations = (conversations: any[]) => {
  const categories: Record<string, any[]> = {
    Today: [],
    Yesterday: [],
    "Past Week": [],
    "Last Month": [],
    Older: [],
  };

  const now = new Date();
  conversations.forEach((conversation) => {
    const conversationDate = new Date(conversation.updated_at);
    const diffInDays = Math.floor(
      (now.getTime() - conversationDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (diffInDays === 0) {
      categories.Today.push(conversation);
    } else if (diffInDays === 1) {
      categories.Yesterday.push(conversation);
    } else if (diffInDays <= 7) {
      categories["Past Week"].push(conversation);
    } else if (diffInDays <= 30) {
      categories["Last Month"].push(conversation);
    } else {
      categories.Older.push(conversation);
    }
  });

  return categories;
};

export interface Chat {
  prompt: string;
  response: string[];
}

export const getMessageByConvoId = async (conversation_id: string) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/conversation/${conversation_id}/messages`,
      {
        headers: DEFAULT_HEADERS
      }
    );
    
    const messages = response.data.messages || [];
    let transformedChats: Chat[] = [];
    
    // Group messages into prompt-response pairs
    for (let i = 0; i < messages.length; i++) {
      if (messages[i].role === 'user') {
        transformedChats.push({
          prompt: messages[i].content,
          response: messages[i + 1]?.role === 'assistant' ? [messages[i + 1].content] : []
        });
      }
    }

    // Return array of chat objects
    return {
      chats: transformedChats
    };
  } catch (error) {
    console.error('Error fetching messages:', error);
    throw error;
  }
};

export async function fetchWorkflowSteps(userInput: string) {
  const response = await fetch(
    `${API_BASE_URL}/workflow/security_analysis?stream=true`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-API-Key": "dev-key",
      },
      body: JSON.stringify({
        user_input: userInput,
        conversationid: ""
      }),
    }
  );

  if (!response.body) {
    console.error("ReadableStream not supported");
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let result = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    const chunk = decoder.decode(value, { stream: true });
    result += chunk;
    // Show partial data
    console.log(chunk);
    // Optional: update UI state with result so far
  }

  console.log("Stream complete:", result);
  return result;
}

export const fetchTableData = async (userInput: string,taskName:string, signal?: AbortSignal) => {
  try {
    
    const response = await fetch(`${API_BASE_URL}/task/${taskName}?stream=true`, {
      method: 'POST',
     headers: {
        "Content-Type": "application/json",
        "X-API-Key": "dev-key",
      },

      body: JSON.stringify({ 
        user_input: userInput 
      }),
      signal
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error('Response body is null');

    return new ReadableStream({
      async start(controller) {
        const decoder = new TextDecoder();
        let buffer = '';

        try {
          while (true) {
            const { done, value } = await reader.read();
            
            if (done) {
              if (buffer) {
                try {
                  const data = JSON.parse(buffer);
                  controller.enqueue(data);
                } catch (e) {
                  console.error('Error parsing final buffer:', e);
                }
              }
              break;
            }

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
              if (line.trim()) {
                try {
                  const data = JSON.parse(line);
                  controller.enqueue(data);
                } catch (e) {
                  console.error('Error parsing stream data:', e);
                }
              }
            }
          }
        } catch (error) {
          controller.error(error);
        } finally {
          reader.releaseLock();
          controller.close();
        }
      },
      cancel() {
        reader.cancel();
      }
    });

  } catch (error) {
    console.error('Error fetching table data:', error);
    throw error;
  }
};